import { colors } from "@styles/colors";
import { useOrder } from "@utils/store/cart/useOrder";
import { getReservableDays, isSlotPast, slotAvailable, todayStr } from "@utils/func/deliverySlots";
import { useEffect, useMemo } from "react";
import { RiCheckboxCircleFill, RiTimeLine } from "react-icons/ri";
import { ScrollView, Text, XStack, YStack } from "tamagui";
import { isGasSkinV2, v2 } from "../skin";

const WD = ["日", "一", "二", "三", "四", "五", "六"];

/** 日期字串 → 星期幾 */
function weekday(dateStr: string): string {
  const [y, mo, d] = dateStr.split("-").map(Number);
  return WD[new Date(y, mo - 1, d).getDay()];
}

/**
 * 配送時間選擇器：採用 kiosk 的橫向日期條 + 清楚時段列樣式（以 GasShop 色票呈現）。
 * 共用 useOrder 的 deliverySlots / deliveryTime。
 */
const DeliverTimeCard = () => {
  const { deliverySlots, deliveryTime, setDeliveryTime, isOpenNow, allowReservation } = useOrder();
  const skinV2 = isGasSkinV2();

  // 可選日期：API 有回、非整日公休、且至少一個可用時段（與入口閘門共用同一判斷）
  const days = useMemo(() => getReservableDays(deliverySlots), [deliverySlots]);

  // 目前可用的配送方式：立即僅營業中(isOpenNow)可用；預約僅瓦斯行開放預約(allowReservation)可用。
  const canScheduled = allowReservation;
  const canImmediate = isOpenNow;

  // 有效配送方式（correct-by-construction）：store 內選取的 type 若目前不可用，直接落到另一種可用方式來「渲染」，
  // 確保未開放預約時「絕不」顯示日期/時段區（不依賴 effect 是否已 commit）。
  //（兩者皆不可用時外層入口閘門已擋成「尚未營業」，DeliverTimeCard 不會被渲染。）
  const effectiveType: "scheduled" | "immediate" =
    deliveryTime.type === "scheduled" && !canScheduled
      ? "immediate"
      : deliveryTime.type === "immediate" && !canImmediate
        ? "scheduled"
        : deliveryTime.type;

  // 把修正後的 type 同步回 store，讓 deliveryTime.tsx 的 isValid / 後續結帳判斷與畫面一致。
  useEffect(() => {
    if (deliveryTime.type !== effectiveType) {
      setDeliveryTime({ ...deliveryTime, type: effectiveType, date: null, slotId: null, slotSource: null, slotLabel: null });
    }
  }, [effectiveType]);

  // 預約模式下，若尚未選日期，預設選第一個可用日（僅在預約可用時）
  useEffect(() => {
    if (effectiveType === "scheduled" && canScheduled && !deliveryTime.date && days.length > 0) {
      setDeliveryTime({ ...deliveryTime, date: days[0], slotId: null, slotSource: null, slotLabel: null });
    }
    // 只在可選日清單 / 有效方式變動時補預設
  }, [days, effectiveType]);

  const selectedDay = (deliverySlots || []).find((d) => d.delivery_date === deliveryTime.date) || null;

  // 配送方式選項：預約配送僅在瓦斯行開放預約時出現；立即配送僅在營業中出現。
  //   開放預約+營業：預約+立即；開放預約+公休：只有預約；未開放預約+營業：只有立即。
  const deliveryOptions = [
    ...(canScheduled ? [{ t: "scheduled" as const, label: "預約配送", sub: "指定日期與時段" }] : []),
    ...(canImmediate ? [{ t: "immediate" as const, label: "立即配送", sub: "將儘速為您配送" }] : []),
  ];

  const setType = (type: "scheduled" | "immediate") =>
    setDeliveryTime({ ...deliveryTime, type, date: null, slotId: null, slotSource: null, slotLabel: null });
  const pickDate = (date: string) => setDeliveryTime({ ...deliveryTime, date, slotId: null, slotSource: null, slotLabel: null });
  const pickSlot = (s: any) =>
    setDeliveryTime({ ...deliveryTime, slotId: s.slot_id, slotSource: s.slot_source, slotLabel: `${s.start_time} - ${s.end_time}` });

  return (
    <YStack gap={16}>
      {/* 配送方式 */}
      <XStack gap={11}>
        {deliveryOptions.map((o) => {
          const sel = effectiveType === o.t;
          return (
            <YStack
              key={o.t}
              flex={1}
              ai="center"
              py={skinV2 ? 14 : 16}
              borderRadius={skinV2 ? 18 : 16}
              borderWidth={skinV2 ? 1.5 : 2}
              borderColor={sel ? colors.primary : skinV2 ? "transparent" : colors.border}
              backgroundColor={sel ? (skinV2 ? v2.primarySoft : colors.primaryActive) : skinV2 ? v2.chipBg : "#fff"}
              cursor="pointer"
              onPress={() => setType(o.t)}
              pressStyle={{ scale: 0.97 }}>
              <Text
                fontSize={15}
                fontWeight={sel ? "800" : skinV2 ? "700" : "800"}
                color={sel ? colors.primary : skinV2 ? v2.chipText : colors.grayDark}>
                {o.label}
              </Text>
              <Text fontSize={11} color={sel && skinV2 ? colors.primary : colors.gray} opacity={sel && skinV2 ? 0.75 : 1} mt={3}>
                {o.sub}
              </Text>
            </YStack>
          );
        })}
      </XStack>

      {effectiveType === "immediate" ? (
        <YStack
          backgroundColor={skinV2 ? "#f8fafc" : "#f4f6fb"}
          borderRadius={skinV2 ? 18 : 14}
          px={16}
          py={skinV2 ? 34 : 16}
          minHeight={skinV2 ? 190 : undefined}
          ai="center"
          jc="center"
          gap={skinV2 ? 10 : 6}>
          <RiTimeLine size={skinV2 ? 42 : 34} color={colors.primary} />
          <Text fontSize={14} fontWeight="700" color={colors.text}>
            下單後將儘速為您安排配送人員
          </Text>
          <Text fontSize={12} color={colors.gray} textAlign="center">
            有任何問題可以聯絡瓦斯行
          </Text>
        </YStack>
      ) : (
        <>
          {/* 目前可選範圍的年月 */}
          {(() => {
            const ref = deliveryTime.date || days[0] || "";
            if (!ref) return null;
            return (
              <Text fontSize={13} fontWeight="700" color={colors.text}>
                {ref.slice(0, 4)} 年 {Number(ref.slice(5, 7))} 月
              </Text>
            );
          })()}
          {/* 橫向日期條 */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <XStack gap={8} pb={2}>
              {days.map((d) => {
                const sel = deliveryTime.date === d;
                const isToday = d === todayStr();
                const dayNum = Number(d.split("-")[2]);
                return (
                  <YStack
                    key={d}
                    width={52}
                    py={9}
                    ai="center"
                    borderRadius={13}
                    borderWidth={2}
                    borderColor={sel ? colors.primary : colors.border}
                    backgroundColor={sel ? colors.primary : "#fff"}
                    cursor="pointer"
                    onPress={() => pickDate(d)}
                    pressStyle={{ scale: 0.93 }}>
                    <Text fontSize={11} color={sel ? "#fff" : colors.text}>
                      {isToday ? "今天" : weekday(d)}
                    </Text>
                    <Text fontSize={18} fontWeight="800" mt={2} color={sel ? "#fff" : colors.grayDark}>
                      {dayNum}
                    </Text>
                  </YStack>
                );
              })}
              {days.length === 0 && (
                <Text fontSize={13} color={colors.gray} py={10}>
                  目前無可預約日期
                </Text>
              )}
            </XStack>
          </ScrollView>

          {/* 時段 */}
          {deliveryTime.date && (
            <YStack gap={9}>
              {(selectedDay?.slots || []).map((s: any) => {
                const avail = slotAvailable(deliveryTime.date!, s);
                const sel = deliveryTime.slotId === s.slot_id;
                return (
                  <XStack
                    key={s.slot_id}
                    ai="center"
                    jc="space-between"
                    px={14}
                    py={14}
                    borderRadius={14}
                    borderWidth={2}
                    borderColor={sel ? colors.primary : colors.border}
                    backgroundColor={sel ? colors.primaryActive : "#fff"}
                    opacity={avail ? 1 : 0.45}
                    cursor={avail ? "pointer" : "not-allowed"}
                    onPress={avail ? () => pickSlot(s) : undefined}
                    pressStyle={avail ? { scale: 0.98 } : undefined}>
                    <XStack ai="center" gap={9}>
                      <RiTimeLine size={18} color={sel ? colors.primary : colors.gray} />
                      <Text fontSize={15} fontWeight={sel ? "800" : "700"} color={sel ? colors.primary : colors.grayDark}>
                        {s.start_time} - {s.end_time}
                      </Text>
                    </XStack>
                    <XStack ai="center" gap={8}>
                      <Text fontSize={12} color={avail ? colors.gray : colors.danger}>
                        {avail
                          ? s.available_capacity !== undefined
                            ? `剩餘 ${s.available_capacity}`
                            : "可預約"
                          : isSlotPast(deliveryTime.date!, s.end_time)
                            ? "已過時段"
                            : "已額滿"}
                      </Text>
                      {sel && <RiCheckboxCircleFill color={colors.primary} size={16} />}
                    </XStack>
                  </XStack>
                );
              })}
              {(!selectedDay?.slots || selectedDay.slots.length === 0) && (
                <Text fontSize={13} color={colors.gray} textAlign="center" py={6}>
                  當日無可選時段
                </Text>
              )}
            </YStack>
          )}
        </>
      )}
    </YStack>
  );
};

export default DeliverTimeCard;
